import type { FC } from 'react'
import type { Industry } from '@/constants/home'

import { useEffect, useState } from 'react'
import { Check, Copy, Shuffle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { hand } from '@/lib/fonts'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { hotTakes, industries } from '@/constants/home'

export const HotTakeGenerator: FC = () => {
    const [industry, setIndustry] = useState<Industry>('Food')
    const [index, setIndex] = useState(0)
    const [spins, setSpins] = useState(0)
    const [copied, setCopied] = useState(false)
    const takes = hotTakes[industry]
    const take = takes[index % takes.length]

    useEffect(() => {
        if (!copied) return

        const timer = window.setTimeout(() => setCopied(false), 2200)

        return () => window.clearTimeout(timer)
    }, [copied])

    const shuffle = () => {
        setIndex(current => {
            if (takes.length < 2) return current

            let next = current

            while (next === current) next = Math.floor(Math.random() * takes.length)

            return next
        })
        setSpins(value => value + 1)
        setCopied(false)
    }

    const steal = async () => {
        try {
            await navigator.clipboard.writeText(`${take} (via Updraft's Hot Take Generator)`)
            setCopied(true)
        }
        catch {
            setCopied(false)
        }
    }

    return (
        <section
            id='hot-takes'
            aria-labelledby='hot-takes-title'
            className='py-20 md:py-28'
        >
            <div className='mx-auto grid max-w-[1400px] items-center gap-12 px-5 md:px-10 lg:grid-cols-12'>
                <div className='lg:col-span-5'>
                    <SectionLabel index='14'>Free ideas</SectionLabel>
                    <SplitText
                        as='h2'
                        id='hot-takes-title'
                        lines={['Hot Take', 'Generator']}
                        italic='hot'
                        className='mt-5 text-[clamp(3rem,6.4vw,6rem)] font-bold leading-[0.88] tracking-[-0.045em]'
                    />
                    <Reveal
                        as='p'
                        delay={150}
                        className='mt-6 max-w-md text-lg leading-relaxed text-stone'
                    >
                        Bold one-line content ideas for your industry. Shuffle until one makes you slightly nervous, then steal it. We genuinely do not mind.
                    </Reveal>

                    <Reveal
                        delay={250}
                        className='mt-8'
                    >
                        <p
                            id='take-industry'
                            className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'
                        >
                            Pick your industry
                        </p>
                        <div
                            role='radiogroup'
                            aria-labelledby='take-industry'
                            className='mt-3 flex flex-wrap gap-2'
                        >
                            {industries.map(item => (
                                <button
                                    key={item}
                                    type='button'
                                    role='radio'
                                    aria-checked={industry === item}
                                    onClick={() => {
                                        setIndustry(item)
                                        setIndex(0)
                                        setSpins(value => value + 1)
                                        setCopied(false)
                                    }}
                                    className={cn(
                                        'rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300',
                                        industry === item ? 'border-ink bg-ink text-cream' : 'border-ink/15 text-ink hover:border-ink/40'
                                    )}
                                >
                                    {item}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                </div>

                <Reveal
                    delay={150}
                    settle={1.5}
                    className='lg:col-span-7'
                >
                    <div className='relative overflow-hidden rounded-[2rem] bg-ember p-7 shadow-[0_40px_80px_-50px_rgb(255_92_0/0.9)] md:p-12'>
                        <span
                            aria-hidden='true'
                            className='tape -top-2 left-12 -rotate-3'
                        />
                        <div className='flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.16em] text-ink/70'>
                            <span>Hot take no. {String((index % takes.length) + 1).padStart(2, '0')} · {industry}</span>
                            <span className='stamp rotate-3 text-ink'>Spicy</span>
                        </div>

                        <p
                            key={`${industry}-${index}-${spins}`}
                            aria-live='polite'
                            className='pop-up mt-10 min-h-[9.5rem] font-heading text-[clamp(2rem,3.8vw,3.4rem)] font-bold leading-[1.02] tracking-[-0.04em] text-ink md:min-h-[11rem]'
                        >
                            {take}
                        </p>

                        <div className='mt-10 flex flex-wrap items-center gap-3'>
                            <button
                                type='button'
                                onClick={steal}
                                className='inline-flex h-12 items-center gap-2 rounded-full bg-ink px-6 text-sm font-medium text-cream transition-colors hover:bg-black'
                            >
                                {copied ? <Check className='size-4 text-ember' /> : <Copy className='size-4' />}
                                {copied ? 'Copied. It is yours now.' : 'Steal this'}
                            </button>
                            <button
                                type='button'
                                onClick={shuffle}
                                className='group inline-flex h-12 items-center gap-2 rounded-full border border-ink/25 px-6 text-sm font-medium text-ink transition-colors hover:border-ink'
                            >
                                <Shuffle className='size-4 transition-transform duration-500 ease-expo group-hover:rotate-180' />
                                Shuffle
                            </button>
                            <p className={cn(hand.className, 'ml-auto hidden rotate-[-4deg] text-2xl text-ink sm:block')}>
                                {takes.length} takes per industry
                            </p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
